/**
 * Time bar — spec §F5.10: "Time bar (bottom-centre, draggable): play/pause 900 ms, slider,
 * year ticks, mono year; visible only when an indicator is mapped."
 *
 * Markup and ids are the mockup's `#timebar` (`#playBtn`, `#yearSlider`, `#yearTicks`,
 * `#yearLabel`), and like the mockup the **whole bar** is its own drag handle — the
 * `lib/draggable.ts` guard skips pointerdowns that land on a button, input or label, so
 * play and the slider keep working.
 *
 * The year lives in the §F1 store (`yearIdx`), so play/slider/ticks all move the same
 * field the choropleth (§F5.3), the unit profile (§F5.7) and the dock popover (§F5.9)
 * already subscribe to.
 *
 * **The "Cumulative" checkbox** (NOTES #361) is the bar's second control and the same kind
 * of thing: it moves one store field (`cumulative`) that every reader of a value subscribes
 * to. It says how a year is read — the year's own reporting, or the running total up to it
 * — and it exists because the stored series is now annual. MELS publishes B.2.1 as a
 * programme-to-date figure and the sync used to store that at every year, so the slider
 * could only ever animate a curve that climbed; the accumulation is still the number on
 * MELS' dashboard, so it stays available rather than being taken away. It is rendered only
 * for an `is_cumulative` indicator — see below.
 *
 * "Visible only when an indicator is mapped" is a render guard, and because a guard is
 * *not* an unmount the play interval is stopped explicitly when the indicator clears —
 * otherwise an invisible time bar would keep animating the year.
 *
 * ## Behind the Animate tool (client, 2026-09-15)
 *
 * > *"the time slider/year slider [should be] rather a drop down ... we can however have an
 * > animation slider, but that can be a button as part of the buttons on the right, which
 * > opens the animation slider."*
 *
 * The everyday control is now `#yearSelect`, next to the Indicator select in the Indicators
 * pane (`IndicatorsPane.tsx`) — it sets the same `yearIdx` and pins the year exactly as the
 * slider does. This bar stays for the animation: `state.animateOpen`, switched by the map
 * tool column's `#btnAnimate`, gates its render in addition to the indicator check above, and
 * its own ✕ (`#timebarClose`) calls `setAnimateOpen(false)`. The markup and every id below
 * are unchanged — the permalink and e2e do not know the bar moved behind a switch.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { fetchIndicators, queryKeys } from '../../lib/api';
import { useDraggable } from '../../lib/draggable';
import { Icon } from '../../lib/icons';
import { selectYear, useAppStore } from '../../lib/state';

/** §F5.10 "play/pause 900 ms". */
export const PLAY_INTERVAL_MS = 900;

export function TimeBar() {
  const { t } = useTranslation();
  const indicator = useAppStore((s) => s.indicator);
  const years = useAppStore((s) => s.years);
  const yearIdx = useAppStore((s) => s.yearIdx);
  const year = useAppStore(selectYear);
  const setYearIdx = useAppStore((s) => s.setYearIdx);
  const snapYearToData = useAppStore((s) => s.snapYearToData);
  const applyDefaultYear = useAppStore((s) => s.applyDefaultYear);
  const cumulative = useAppStore((s) => s.cumulative);
  const setCumulative = useAppStore((s) => s.setCumulative);
  const animateOpen = useAppStore((s) => s.animateOpen);
  const setAnimateOpen = useAppStore((s) => s.setAnimateOpen);
  const [playing, setPlaying] = useState(false);
  const barRef = useRef<HTMLDivElement | null>(null);

  useDraggable(barRef, barRef);

  /**
   * §D3 `indicators[].years` — which ticks mean anything for the indicator on the map.
   * `b21` carries 2025–26 and nothing else since the demo purge, and a tick that silently
   * paints an empty map reads as a broken portal rather than as an unpublished year.
   *
   * An indicator the catalogue has not answered for yet — or one with no values at all —
   * yields an empty list, and an empty list disables **nothing**: refusing to render ticks
   * on a slow boot would be a worse lie than showing all six.
   */
  const { data: indicators } = useQuery({
    queryKey: queryKeys.indicators(),
    queryFn: fetchIndicators,
  });
  const meta = useMemo(
    () => indicators?.find((i) => i.id === indicator) ?? null,
    [indicators, indicator],
  );
  const withData = useMemo(() => meta?.years ?? [], [meta]);
  const hasData = useCallback(
    (y: number) => withData.length === 0 || withData.includes(y),
    [withData],
  );

  /**
   * §D3 `indicators[].default_year` — the year a cold load should *open* on. Distinct from
   * `years`: 2026 has data for `b21` and is therefore a live tick, but it holds three rows
   * against 2025's eighty, so booting there painted one polygon on an otherwise empty map.
   * A permalink `y=` and any move of the slider pin the year, and `applyDefaultYear` is a
   * no-op from then on — see `state.yearPinned`.
   */
  const defaultYear = meta?.default_year ?? null;
  useEffect(() => {
    applyDefaultYear(defaultYear);
  }, [defaultYear, applyDefaultYear]);

  // Picking an indicator whose years do not include the reader's current one moves the bar
  // rather than emptying the map — to that indicator's own `default_year` where it has one.
  // Runs on every change of the *set*, so it also catches a sync that publishes a new year
  // while the page is open.
  useEffect(() => {
    snapYearToData(withData, defaultYear);
  }, [withData, defaultYear, snapYearToData]);

  // Clearing the indicator hides the bar; it must also leave it stopped, so that mapping
  // the next indicator does not silently resume an animation nobody can see paused.
  useEffect(() => {
    if (!indicator) setPlaying(false);
  }, [indicator]);

  useEffect(() => {
    if (!playing || !indicator) return;
    const id = window.setInterval(() => {
      // Read through the store, not the closure: the user may also be dragging the slider.
      const s = useAppStore.getState();
      // §F5.10 loops back to the first year rather than stopping at the last — but skips
      // the years this indicator has no data for, so play animates the series instead of
      // blinking the map empty for four of every six frames.
      const total = s.years.length;
      for (let step = 1; step <= total; step += 1) {
        const next = (s.yearIdx + step) % total;
        const candidate = s.years[next];
        if (candidate !== undefined && hasData(candidate)) {
          s.setYearIdx(next);
          return;
        }
      }
    }, PLAY_INTERVAL_MS);
    return () => window.clearInterval(id);
    // `hasData` is a dependency, not an oversight: the interval closes over it, so a sync
    // that publishes a new year mid-session must restart the interval or play keeps
    // skipping the year that has just appeared.
  }, [playing, indicator, hasData]);

  // Nothing to animate without a mapped indicator (§F5.10).
  if (!indicator) return null;
  // Client, 2026-09-15: the bar is behind the Animate map tool (`#btnAnimate`) — `#yearSelect`
  // (next to the Indicator select) is the everyday control now, and this bar only draws once
  // the reader has explicitly asked for the animation view. The play loop's own cleanup
  // effects above still run regardless, exactly as they already do for a cleared indicator.
  if (!animateOpen) return null;

  return (
    <div id="timebar" ref={barRef}>
      <button
        type="button"
        className="mapbtn"
        id="playBtn"
        style={{ width: 30, height: 30 }}
        aria-pressed={playing}
        aria-label={playing ? t('timebar.pause') : t('timebar.play')}
        title={playing ? t('timebar.pause') : t('timebar.play')}
        onClick={() => setPlaying((p) => !p)}
      >
        <Icon name={playing ? 'pause' : 'play'} size={15} />
      </button>

      <div className="tcol">
        <input
          type="range"
          id="yearSlider"
          min={0}
          max={years.length - 1}
          step={1}
          value={yearIdx}
          aria-label={t('timebar.year')}
          aria-valuetext={String(year)}
          onChange={(e) => setYearIdx(Number(e.target.value))}
        />
        <div className="ticks" id="yearTicks">
          {years.map((y, i) => {
            const empty = !hasData(y);
            return (
              <button
                type="button"
                key={y}
                data-year={y}
                data-empty={empty ? '' : undefined}
                className={i === yearIdx ? 'active' : ''}
                aria-pressed={i === yearIdx}
                disabled={empty}
                title={empty ? t('timebar.noData', { year: y }) : t('timebar.goTo', { year: y })}
                onClick={() => setYearIdx(i)}
              >
                {y}
              </button>
            );
          })}
        </div>
      </div>

      <div className="yr" id="yearLabel">
        {year}
      </div>

      {/* Only for a series that accumulates. For anything else the box would be a control
          with no effect — the server ignores `mode=` there and `modeFor` never sends it —
          and a dead checkbox next to the slider reads as a broken portal. */}
      {meta?.is_cumulative ? (
        <label className="tb-cum" id="cumToggle" title={t('timebar.cumulativeTitle')}>
          <input
            type="checkbox"
            data-cumulative=""
            checked={cumulative}
            onChange={(e) => setCumulative(e.target.checked)}
          />
          {t('timebar.cumulative')}
        </label>
      ) : null}

      <button
        type="button"
        className="mapbtn"
        id="timebarClose"
        style={{ width: 24, height: 24 }}
        aria-label={t('timebar.close')}
        title={t('timebar.close')}
        onClick={() => setAnimateOpen(false)}
      >
        <Icon name="x" size={13} />
      </button>
    </div>
  );
}

export default TimeBar;
